// src/layouts/TeamLayouts.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  NavLink,
  Outlet,
  useParams,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import {
  Megaphone,
  CalendarClock,
  CalendarDays,
  Users,
  BarChart2,
  ChevronLeft,
  Menu,
  Shield,
  LayoutDashboard,
  MessageCircle,
  Settings,
  Building2,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import ThemeToggle from "../components/ThemeToggle";

type NavItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function getStorageUrl(bucket: string, path: string) {
  if (!path) return "";
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

export default function TeamLayout() {
  const { clubId, teamId } = useParams<{ clubId: string; teamId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  /** ✅ pas d'early return avant les hooks */
  const safeClubId = clubId ?? "unknown";
  const safeTeamId = teamId ?? "unknown";
  const base = `/club/${safeClubId}/team/${safeTeamId}`;
  const clubBase = `/club/${safeClubId}`;

  const [teamName, setTeamName] = useState("Équipe");
  const [clubLogoPath, setClubLogoPath] = useState("");

  useEffect(() => {
    if (!teamId) return;
    supabase
      .from("teams")
      .select("team_name")
      .eq("id", teamId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.team_name) setTeamName(data.team_name.trim());
      });
  }, [teamId]);

  useEffect(() => {
    if (!clubId) return;
    supabase
      .from("clubs")
      .select("logo_path")
      .eq("id", clubId)
      .maybeSingle()
      .then(({ data }) => {
        setClubLogoPath(data?.logo_path || "");
      });
  }, [clubId]);

  const logoSrc = clubLogoPath ? getStorageUrl("club-logos", clubLogoPath) : "";

  const navItems: NavItem[] = useMemo(
    () => [
      { label: "Fil d’actu", to: `${base}`, icon: <Megaphone className="h-4 w-4" /> },
      { label: "Événements", to: `${base}/events`, icon: <CalendarClock className="h-4 w-4" /> },
      { label: "Calendrier", to: `${base}/calendar`, icon: <CalendarDays className="h-4 w-4" /> },
      { label: "Membres", to: `${base}/members`, icon: <Users className="h-4 w-4" /> },
      { label: "Statistiques", to: `${base}/stats`, icon: <BarChart2 className="h-4 w-4" /> },
      { label: "Messagerie", to: `${base}/messages`, icon: <MessageCircle className="h-4 w-4" /> },
      { label: "Paramètres", to: `${base}/settings`, icon: <Settings className="h-4 w-4" /> },
    ],
    [base]
  );

  const currentTitle = useMemo(() => {
    const found = navItems.find(
      (it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/")
    );
    return found?.label ?? "Équipe";
  }, [navItems, location.pathname]);

  /** ✅ ensuite seulement */
  if (!clubId || !teamId) return <Navigate to="/" replace />;

  const Sidebar = (
    <aside className="h-full w-72 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col">
      {/* Top / back */}
      <div className="p-4 pt-safe-top pl-safe-left border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-start gap-3">
          <NavLink
            to={`${clubBase}/teams`}
            onClick={() => setMobileOpen(false)}
            className={cx(
              "inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-800",
              "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            )}
            aria-label="Retour au club"
          >
            <ChevronLeft className="h-4 w-4" />
          </NavLink>

          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              className="h-9 w-9 rounded-lg object-cover border border-gray-200 dark:border-gray-800 shrink-0"
            />
          ) : (
            <div className="h-9 w-9 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
              <Building2 className="h-4 w-4 text-gray-400" />
            </div>
          )}

          <div className="min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">Équipe</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {teamName}
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
              {currentTitle}
            </div>
          </div>
        </div>

        <NavLink
          to={`${clubBase}/dashboard`}
          onClick={() => setMobileOpen(false)}
          className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
        >
          <Shield className="h-4 w-4 text-gray-500" />
          Retour au club (dashboard)
        </NavLink>
      </div>

      {/* Nav */}
      <nav className="p-3 space-y-1 flex-1 overflow-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === base}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cx(
                "flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition",
                "hover:bg-gray-50 dark:hover:bg-gray-800",
                isActive
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  : "text-gray-700 dark:text-gray-200"
              )
            }
          >
            <span className="text-gray-600 dark:text-gray-300">{item.icon}</span>
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-800 space-y-3">
        {/* ✅ Bouton App Coach (interne) */}
        <button
          type="button"
          onClick={() => {
            setMobileOpen(false);
            navigate("/Dashboard");
          }}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800
                     bg-gray-900 text-white hover:bg-black
                     dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100
                     text-sm transition"
          title="Accéder à l’app Coach"
        >
          <LayoutDashboard className="h-4 w-4" />
          App Coach
        </button>

        <div className="text-xs text-gray-500 dark:text-gray-400">
          Team ID : <span className="font-mono">{teamId}</span>
        </div>

        {/* ✅ Toggle thème */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">Thème</div>
          <ThemeToggle />
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-[calc(100vh-0px)] bg-gray-50 dark:bg-gray-950">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 pt-safe-top pl-safe-left pr-safe-right">
        <div className="flex items-center justify-between p-3">
          <button
            type="button"
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-100"
            onClick={() => setMobileOpen(true)}
            aria-label="Ouvrir le menu équipe"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="min-w-0 px-2 flex-1 flex items-center justify-center gap-2">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                className="h-7 w-7 rounded-md object-cover border border-gray-200 dark:border-gray-800 shrink-0"
              />
            ) : (
              <div className="h-7 w-7 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                <Building2 className="h-3.5 w-3.5 text-gray-400" />
              </div>
            )}
            <div className="min-w-0 text-center">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {teamName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {currentTitle}
              </div>
            </div>
          </div>

          <NavLink
            to={`${clubBase}/teams`}
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-100"
            aria-label="Retour"
          >
            <ChevronLeft className="h-5 w-5" />
          </NavLink>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72">{Sidebar}</div>
        </div>
      )}

      {/* Desktop layout */}
      <div className="hidden md:flex min-h-[calc(100vh-0px)]">
        <div className="sticky top-0 h-screen">{Sidebar}</div>

        <main className="flex-1">
          {/* Header desktop */}
          <div className="sticky top-0 z-20 bg-gray-50/80 dark:bg-gray-950/80 backdrop-blur border-b border-gray-200 dark:border-gray-800">
            <div className="px-6 py-4 text-center">
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {teamName} • {currentTitle}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                Espace équipe
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile content */}
      <div className="md:hidden p-4 pb-safe-bottom pl-safe-left pr-safe-right">
        <Outlet />
      </div>
    </div>
  );
}
